// Find-and-highlight helpers shared by all viewers.
//
// Two distinct visual states are produced:
//   - `cite-highlight`     — the verified Ask Arc snippet that the
//                            citation jumped to (one-shot, on open).
//   - `search-highlight`   — matches from the in-doc search box.
//                            Multiple, navigable via next/prev.
// They use different CSS classes so the two never visually collide.

export const CITE_CLASS = 'cite-highlight';
export const SEARCH_CLASS = 'search-highlight';
export const SEARCH_ACTIVE_CLASS = 'search-highlight--active';

// Unicode folding applied to BOTH needle and haystack before any pass:
//   - NFKC (ligatures ﬁ→fi, fullwidth forms) — PDF text layers and
//     extracted text routinely disagree on these, and a single ligature
//     used to kill an entire 2000-char match.
//   - smart quotes → ASCII quotes
//   - soft hyphens dropped
// Returns a string that may be longer or shorter than the input — use
// foldChar for per-character mapping (findTextMatches needs alignment).
const QUOTE_FOLD = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'",
  '“': '"', '”': '"', '„': '"', '‟': '"',
  '′': "'", '″': '"',
};
const SOFT_HYPHEN = '­';

// Fold ONE source character into its match form (possibly '', possibly
// multiple chars). Kept per-character so the DOM char map in
// findTextMatches stays aligned to source offsets.
export function foldChar(ch) {
  if (ch === SOFT_HYPHEN) return '';
  const q = QUOTE_FOLD[ch];
  if (q) return q;
  // NFKC is a no-op for ASCII — only pay for it on non-ASCII chars.
  if (ch.charCodeAt(0) < 128) return ch.toLowerCase();
  return ch.normalize('NFKC').toLowerCase();
}

function foldString(s) {
  let out = '';
  for (const ch of (s || '')) out += foldChar(ch);
  return out;
}

// Normalize whitespace + unicode-fold + lowercase for fuzzy substring
// matching. We do NOT strip punctuation — the snippet comes from the
// verified tool result so punctuation usually matches, and stripping it
// would let false-positive substrings creep in.
export function normalizeForMatch(s) {
  return foldString(s).replace(/\s+/g, ' ').trim();
}

// Strip ALL whitespace. Used as a fallback when the normal whitespace-
// preserving search comes up empty — this is the case for OCR PDFs where
// every character is its own item/span, so word boundaries don't line up
// between haystack and needle.
export function denseNormalize(s) {
  return foldString(s).replace(/\s+/g, '');
}

// Strip ALL non-alphanumerics (whitespace AND punctuation). Used as the
// LAST-ditch fallback when even dense matching fails — this happens when
// the canonical extracted text and the rendered PDF.js text layer disagree
// on a single punctuation char (e.g. extractor read "Process: 7A-SU…" as
// "Process - 7A-SU…", inserting a stray hyphen). Without an alphanumeric
// pass the entire 800-char chunk needle becomes unfindable over a one-char
// mismatch. False-positive risk is low because chunk-length needles are
// long enough that a punctuation-stripped collision is unlikely.
export function alnumNormalize(s) {
  return foldString(s).replace(/[^a-z0-9]+/gi, '');
}

// Walk a container's text nodes and find all character ranges matching
// `query`. Tries three passes in order:
//   1) whitespace-collapsed (normal case — well-formed PDFs, plain text)
//   2) whitespace-stripped (OCR PDFs with char-per-item spans, or any
//      case where the rendered DOM has more/fewer spaces than the
//      verified citation snippet)
//   3) alphanumeric-only (recovers from one-char punctuation mismatches
//      between native-PDF extraction and the rendered text layer)
// Each match is a {startNode, startOffset, endNode, endOffset} suitable
// for a Range. Matches CAN span multiple text nodes — wrapMatches below
// handles that.
export function findTextMatches(container, query) {
  if (!container || !query) return [];
  const qNormal = normalizeForMatch(query);
  if (qNormal.length < 2) return [];

  // Pull every text-bearing node once. Includes whitespace-only nodes so
  // the char map stays continuous (otherwise denseNormalize would skip a
  // real char-to-node mapping).
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.nodeValue && n.nodeValue.length > 0 ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });

  // For each character in the source DOM, record (node, offset) plus
  // flags for which haystacks it participates in. ``dense`` is true for
  // any non-whitespace char; ``alnum`` is true only for [a-z0-9].
  const charMap = [];
  let normText = '';   // collapsed-whitespace + lowercase
  let denseText = '';  // no-whitespace + lowercase
  let alnumText = '';  // no-whitespace + no-punctuation + lowercase
  let lastWasWs = false;
  let node = walker.nextNode();
  const ALNUM_RE = /[a-z0-9]/i;
  while (node) {
    const raw = node.nodeValue;
    for (let i = 0; i < raw.length; i++) {
      const ch = raw[i];
      const isWs = /\s/.test(ch);
      if (isWs) {
        // Collapsed: emit a single space across a whitespace run, but
        // still record THIS char in the map so the denser haystacks
        // (which skip whitespace) stay aligned to normText positions.
        if (!lastWasWs) {
          normText += ' ';
          charMap.push({ node, offset: i, dense: false, alnum: false });
        }
        lastWasWs = true;
      } else {
        // Unicode-fold the char. One source char can emit 0..N match
        // chars (soft hyphen → 0, ligature ﬁ → 'f' + 'i'); every
        // emitted char maps back to THIS source offset so wrapping
        // covers the whole source char.
        const folded = foldChar(ch);
        for (let k = 0; k < folded.length; k++) {
          const fch = folded[k];
          normText += fch;
          denseText += fch;
          const isAlnum = ALNUM_RE.test(fch);
          if (isAlnum) alnumText += fch;
          charMap.push({ node, offset: i, dense: true, alnum: isAlnum });
        }
        lastWasWs = false;
      }
    }
    node = walker.nextNode();
  }

  // Match a needle against one of the three haystacks, then walk charMap
  // (filtered by which haystack we're using) to map the indexOf result
  // back to a startNode/endNode range in the original DOM.
  const tryMatch = (haystack, needle, filter) => {
    const out = [];
    if (!haystack || haystack.length < needle.length) return out;
    let from = 0;
    while (from <= haystack.length - needle.length) {
      const idx = haystack.indexOf(needle, from);
      if (idx === -1) break;
      let pos = -1;
      let startEntry = null;
      let endEntry = null;
      for (const entry of charMap) {
        if (filter && !filter(entry)) continue;
        pos++;
        if (pos === idx) startEntry = entry;
        if (pos === idx + needle.length - 1) { endEntry = entry; break; }
      }
      if (startEntry && endEntry) {
        out.push({
          startNode: startEntry.node,
          startOffset: startEntry.offset,
          endNode: endEntry.node,
          endOffset: endEntry.offset + 1,
        });
      }
      from = idx + needle.length;
    }
    return out;
  };

  // Pass 1: normal (whitespace-aware).
  let matches = tryMatch(normText, qNormal, null);
  if (matches.length > 0) return matches;

  // Pass 2: whitespace-stripped.
  const qDense = denseNormalize(query);
  if (qDense.length >= 2) {
    matches = tryMatch(denseText, qDense, (e) => e.dense);
    if (matches.length > 0) return matches;
  }

  // Pass 3: alphanumeric-only.
  const qAlnum = alnumNormalize(query);
  if (qAlnum.length >= 2) {
    matches = tryMatch(alnumText, qAlnum, (e) => e.alnum);
  }
  return matches;
}

// Wrap every text-node slice that overlaps `range` in its own <mark>.
// Unlike Range.surroundContents (which throws on cross-element ranges —
// the common case in pdf.js text layers where each token is its own
// <span>), this walks text nodes inside the range and wraps the
// in-range portion of each. The visual effect is the same: every
// covered character ends up inside a <mark>.
function wrapRange(range, className) {
  const marks = [];
  const { startContainer, startOffset, endContainer, endOffset } = range;

  // Collect every text node that intersects the range.
  const root = range.commonAncestorContainer;
  const candidates = [];
  if (root.nodeType === Node.TEXT_NODE) {
    candidates.push(root);
  } else {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let n = walker.nextNode();
    while (n) { candidates.push(n); n = walker.nextNode(); }
  }

  let inside = false;
  for (const node of candidates) {
    // Reaching the start container flips us on.
    if (node === startContainer) inside = true;
    if (!inside && node !== startContainer && node !== endContainer) continue;

    // Slice the in-range portion of this text node.
    const localStart = node === startContainer ? startOffset : 0;
    const localEnd = node === endContainer ? endOffset : node.nodeValue.length;
    if (localEnd <= localStart) {
      if (node === endContainer) break;
      continue;
    }

    // Split surrounding text out, leaving only the matched slice in `mid`.
    let mid = node;
    if (localEnd < node.nodeValue.length) mid.splitText(localEnd);
    if (localStart > 0) mid = mid.splitText(localStart);

    const mark = document.createElement('mark');
    mark.className = className;
    mid.parentNode.insertBefore(mark, mid);
    mark.appendChild(mid);
    marks.push(mark);

    if (node === endContainer) break;
  }
  return marks;
}

// Wrap each match range in <mark>s with the given class. Each match may
// produce multiple <mark>s when it crosses element boundaries (common
// in pdf.js text layers). Each <mark> gets a `data-match-idx` attribute
// so callers can group them when navigating: the "active match" decoration
// applies to every <mark> with the matching index.
//
// Returns one anchor element per match (the FIRST <mark>) in document
// order, so `anchors[i]` is the scroll target for match i.
//
// Applied in REVERSE document order because each wrap mutates the DOM
// and would invalidate later offsets.
export function wrapMatches(matches, className) {
  if (!matches || matches.length === 0) return [];
  const anchors = new Array(matches.length).fill(null);
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i];
    try {
      const range = document.createRange();
      range.setStart(m.startNode, m.startOffset);
      range.setEnd(m.endNode, m.endOffset);
      const marks = wrapRange(range, className);
      for (const mk of marks) mk.dataset.matchIdx = String(i);
      if (marks[0]) anchors[i] = marks[0];
    } catch {
      // skip — leave the DOM alone
    }
  }
  // Drop any nulls (matches that failed to wrap) but preserve order.
  return anchors.filter(Boolean);
}

// Remove all <mark> wrappers with the given class, restoring the
// original text-node structure. Safe to call when nothing matches.
export function clearMarks(container, className) {
  if (!container) return;
  const marks = container.querySelectorAll(`mark.${className}`);
  marks.forEach((m) => {
    const parent = m.parentNode;
    if (!parent) return;
    while (m.firstChild) parent.insertBefore(m.firstChild, m);
    parent.removeChild(m);
    parent.normalize();
  });
}

// Find the nearest scrollable ancestor (overflow-y auto/scroll with
// actual overflow). Used instead of scrollIntoView's ancestor walk,
// which is unreliable in the viewer's nested overflow containers
// (.dv-body + per-preview panes) — Chrome sometimes scrolls only the
// outer container, leaving the highlight off-screen.
export function scrollableAncestor(el) {
  let p = el?.parentElement;
  while (p) {
    const st = window.getComputedStyle(p);
    if (/(auto|scroll)/.test(st.overflowY) && p.scrollHeight > p.clientHeight + 1) return p;
    p = p.parentElement;
  }
  return null;
}

// Center `el` in its nearest scrollable ancestor with explicit
// scrollTop math. Returns true if a scroll was performed.
export function scrollMarkIntoView(el) {
  if (!el || !el.isConnected) return false;
  const container = scrollableAncestor(el);
  if (!container) {
    el.scrollIntoView({ block: 'center' });
    return true;
  }
  const cRect = container.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  container.scrollTop += r.top - cRect.top - (cRect.height - r.height) / 2;
  return true;
}

// Strip common search-result artifacts from a snippet's edges so the
// haystack match isn't tripped by leading "..." / quote marks / orphan
// punctuation that the source doc doesn't actually contain.
function trimEdges(s) {
  return (s || '').replace(/^[\s"'…….,;:!?\-]+|[\s"'…….,;:!?\-]+$/g, '');
}

// Generate substring candidates from a snippet, in priority order.
//
// With the SOTA wire-up, the snippet that reaches the viewer IS the
// verbatim indexed chunk text — no model paraphrase, no chunker head
// junk, no mid-word truncation. So the primary attempt is always the
// full snippet. We keep ONE half-length prefix as a defensive fallback
// for the rare case where the rendered text layer drops some content
// (e.g., pdf.js can't render a marked-content portion) — the head
// half is still verbatim and should hit.
//
// Exported because the auto-jump scanner uses the same candidates to
// decide which page contains the snippet.
export function snippetCandidates(snippet) {
  const base = trimEdges(snippet);
  if (!base) return [];

  const FLOOR = 20;
  const out = [];
  const seen = new Set();

  const add = (text, side) => {
    if (text.length < FLOOR || seen.has(text)) return;
    seen.add(text);
    out.push({ text, side, length: text.length });
  };

  // Primary: full verbatim text.
  add(base, 'full');
  // Defensive fallback: half-length head. If the full text doesn't
  // match due to a partial rendering gap, the head half usually does.
  const halfLen = Math.max(FLOOR, Math.floor(base.length * 0.5));
  add(trimEdges(base.slice(0, halfLen)), 'head-half');

  return out;
}

// Find + wrap + scroll-into-view in one call.
//
// Primary path: the snippet is verbatim chunk text from the indexer
// (the SOTA citation path). It matches the rendered text layer
// directly. Falls back to a half-length head if the rendering misses
// some tail content.
//
// Returns { marks, attempts, matchedLength, matchedSide } so callers
// can observe which candidate landed (or "none" if everything missed).
export function highlightSnippet(container, snippet, className = CITE_CLASS, { scroll = true } = {}) {
  if (!container || !snippet) {
    return { marks: [], attempts: [], matchedLength: null, matchedSide: null };
  }
  clearMarks(container, className);

  const candidates = snippetCandidates(snippet);
  if (candidates.length === 0) {
    return { marks: [], attempts: [], matchedLength: null, matchedSide: null };
  }

  const attempts = [];
  for (const c of candidates) {
    const matches = findTextMatches(container, c.text);
    attempts.push({ length: c.length, side: c.side, matchCount: matches.length });
    if (matches.length === 0) continue;
    const marks = wrapMatches(matches, className);
    if (marks.length === 0) continue;
    if (scroll && marks[0]) {
      marks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return { marks, attempts, matchedLength: c.length, matchedSide: c.side };
  }
  return { marks: [], attempts, matchedLength: null, matchedSide: null };
}
